/**
 * Цвет «Отметки о просмотре». Цвет личный: выбор перекрашивает
 * отмеченные задачи только у выбравшего, остальные участники доски
 * видят свои цвета. Выбор сохраняется сразу, без кнопки «Сохранить».
 *
 * Одна палитра вместо набора образцов: сотрудник подбирает любой цвет
 * в системном окне выбора — пипетка там уже есть, своя кнопка не нужна.
 */

import { useEffect, useRef, useState } from "react";

import type { WsError } from "@taskflow/client-core";
import { cn, toast } from "@/shared/ui";

import { useSetViewMarkColorMutation } from "@taskflow/client-core/boards";
import { DEFAULT_VIEW_MARK_COLOR } from "@taskflow/client-core/boards";

/** Задержка сохранения: пока ползунок палитры тянут, событий — десятки. */
const SAVE_DELAY_MS = 400;

function flushPending(state: {
  pending: string | null;
  timer: ReturnType<typeof setTimeout> | undefined;
  save: (color: string) => Promise<void>;
}) {
  clearTimeout(state.timer);
  const color = state.pending;
  state.pending = null;
  if (color !== null) void state.save(color);
}

export function ViewMarkPalette({
  boardId,
  value,
  disabled = false,
  size = "md",
  label = "Цвет отметки о просмотре",
  title,
}: {
  boardId: string;
  /** Личный цвет смотрящего; null — ещё не выбран. */
  value: string | null;
  disabled?: boolean;
  size?: "sm" | "md";
  label?: string;
  title?: string;
}) {
  const [setColor] = useSetViewMarkColorMutation();
  const current = (value ?? DEFAULT_VIEW_MARK_COLOR).toUpperCase();

  // Черновик — то, что видно, пока тянут ползунок: сохраняется с задержкой.
  const [draft, setDraft] = useState(current);
  // Пришёл новый цвет с сервера (или из кэша после сохранения) — показать его.
  const [seen, setSeen] = useState(current);
  if (seen !== current) {
    setSeen(current);
    setDraft(current);
  }

  const save = async (color: string) => {
    try {
      await setColor({ boardId, color }).unwrap();
    } catch (raw) {
      toast.error(raw as WsError, "Не удалось изменить цвет отметки");
    }
  };

  // Несохранённый выбор и таймер — в одном изменяемом объекте: при
  // закрытии карточки сразу после выбора цвет должен сохраниться, а не
  // потеряться вместе с таймером.
  const saving = useRef<{
    pending: string | null;
    timer: ReturnType<typeof setTimeout> | undefined;
    save: (color: string) => Promise<void>;
  }>({ pending: null, timer: undefined, save });
  useEffect(() => {
    saving.current.save = save;
  });

  const flush = () => flushPending(saving.current);

  useEffect(() => {
    const state = saving.current;
    return () => flushPending(state);
  }, []);

  const pick = (raw: string) => {
    const color = raw.toUpperCase();
    setDraft(color);
    const state = saving.current;
    state.pending = color === current ? null : color;
    clearTimeout(state.timer);
    state.timer = setTimeout(flush, SAVE_DELAY_MS);
  };

  return (
    <div className={cn("flex items-center", size === "sm" ? "gap-1" : "gap-2")}>
      <input
        type="color"
        aria-label={label}
        title={title ?? label}
        // Нативное поле понимает только нижний регистр.
        value={draft.toLowerCase()}
        disabled={disabled}
        onChange={(event) => pick(event.target.value)}
        onBlur={flush}
        className={cn(
          "shrink-0 cursor-pointer appearance-none rounded-full border border-line bg-transparent p-0",
          "[&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0",
          "[&::-moz-color-swatch]:rounded-full [&::-moz-color-swatch]:border-0",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          "disabled:cursor-not-allowed disabled:opacity-50",
          size === "sm" ? "size-5" : "size-8",
        )}
      />
      {size === "md" && (
        <span className="font-mono text-[12px] text-muted tabular-nums">{draft}</span>
      )}
    </div>
  );
}
